import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Switch,
} from '@mui/material';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import withStyles from '@mui/styles/withStyles';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import {gql, useLazyQuery, useMutation, useQuery} from '@apollo/client';
import moment from 'moment';
import {useLocation} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {groupBy} from 'lodash-es';
import {useDebugContext} from '../../contexts/DebugProvider';
import ApiException from '../../utils/exception';
import {useCuroSync} from './useCuroSync';
import Loading from '../Loading/Loading';
import {
  ShowIfRole,
  getBusinessUnitsQuery,
  useDjangoContext,
} from '../../contexts/useAuthContext';
import {
  SwitchBusinessUnit,
  SwitchBusinessUnitVariables,
} from '../../schema-types/SwitchBusinessUnit';
import {GetBusinessUnits} from '../../schema-types/GetBusinessUnits';
import {usePrePrecursorGQL} from '../PrecursorForm/usePrePrecursorGQL';
import {getReviewIdFromPath} from './AdminDialogHelper';
import {useSetIsJourneyToTrue} from '../../hooks/useSetIsJourneyToTrue';
import {GET_LOGIN_INFO_QUERY} from '../../hooks/useDjango';
import validateUUID from '../../utils/validateUUID';

/**
 * Only place where the front-end sets up business units
 * Can be accessed by devs and testers only
 * Should this be removed later on?
 */
export const switchBusinessUnitMutation = gql`
  mutation SwitchBusinessUnit($businessUnitId: UUID, $staffId: UUID!) {
    updateStaff(businessUnitId: $businessUnitId, staffId: $staffId) {
      staff {
        id
        businessUnit {
          id
          name
        }
      }
    }
  }
`;

export const ChangeBusinessUnit: React.FC<{
  type: 'dropdown' | 'buttons';
  _onClose?: () => void;
}> = ({type, _onClose}) => {
  const {djangoUser, businessUnit} = useDjangoContext();
  const [_switchBusinessUnit] = useMutation<
    SwitchBusinessUnit,
    SwitchBusinessUnitVariables
  >(switchBusinessUnitMutation);
  const {data} = useQuery<GetBusinessUnits>(getBusinessUnitsQuery);
  const switchBusinessUnit = (businessUnitId: string) => {
    if (djangoUser.id) {
      _switchBusinessUnit({
        variables: {
          staffId: djangoUser.id,
          businessUnitId,
        },
        refetchQueries: [GET_LOGIN_INFO_QUERY],
      });
    }
  };
  const businessUnitsGrouped = groupBy(data?.businessUnits || [], 'partnershipType');

  return (
    <>
      <Typography variant="h4">Change business unit:</Typography>
      {type === 'dropdown' ? (
        <Select
          // placeholder="Select action"
          onChange={(e: any) => {
            switchBusinessUnit(e.target.value);
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }}
          value={businessUnit?.id}
        >
          {data?.businessUnits?.map((x) => {
            return (
              <MenuItem key={x!.id} value={x?.id}>
                {x?.name}
              </MenuItem>
            );
          })}
        </Select>
      ) : (
        <Box mt={2}>
          {Object.keys(businessUnitsGrouped).map((group) => {
            return (
              <Box mb={1} key={group}>
                <Typography>{group}</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {businessUnitsGrouped[group]?.map((x) => {
                    return (
                      <Button
                        color={x?.id === businessUnit.id ? 'secondary' : 'primary'}
                        variant="contained"
                        size="small"
                        key={x!.id}
                        onClick={(e) => {
                          switchBusinessUnit(String(x?.id));
                          if (e.altKey) {
                            // do nothing
                          } else {
                            setTimeout(() => {
                              window.location.reload();
                            }, 500);
                          }
                        }}
                      >
                        {x?.name}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};

export const SYNC_ACCOUNTS = gql`
  query SyncAccounts {
    fetchMonthlyAccounts
  }
`;

function DebugSwitches() {
  const {debug, setDebug} = useDebugContext();
  const {syncData, getSyncData, startFullSync, fullSyncLoading} = useCuroSync();
  const {enqueueSnackbar} = useSnackbar();
  const {updateReview, updateReviewLoading} = usePrePrecursorGQL();
  const {pathname} = useLocation();
  const reviewId = getReviewIdFromPath(pathname);
  const {setIsJourneyToTrue} = useSetIsJourneyToTrue();

  const [
    onFetchMonthlyAccounts,
    {loading: fetchMonthlyAccountsLoading, data: fetchMonthlyAccountsData, called},
  ] = useLazyQuery(SYNC_ACCOUNTS);

  useEffect(() => {
    // get full curo sync info
    getSyncData();
  }, []);
  const handleChange = (event: any) => {
    const type = event.target.name;
    const newDebug = {...debug, [type]: event.target.checked || false};
    setDebug(newDebug);
  };

  const syncResult = fetchMonthlyAccountsData?.fetchMonthlyAccounts
    ? 'Sync successful'
    : 'Unable to sync';

  return (
    <FormControl component="fieldset">
      <FormLabel component="legend">Enable or disable debug flags</FormLabel>
      <FormGroup>
        <FormControlLabel
          control={<Switch checked={debug?.forms} onChange={handleChange} name="forms" />}
          label="Forms"
        />
        <FormControlLabel
          control={
            <Switch checked={debug?.panels} onChange={handleChange} name="panels" />
          }
          label="Panels"
        />
        <FormControlLabel
          control={
            <Switch checked={debug?.precursor} onChange={handleChange} name="precursor" />
          }
          label="Precursor"
        />
      </FormGroup>
      <Divider />
      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={debug?.editorGridEmpty}
              onChange={handleChange}
              name="editorGridEmpty"
            />
          }
          label="Force editor-grid empty state"
        />
        <FormControlLabel
          control={
            <Switch
              checked={debug?.editorGridFailure}
              onChange={handleChange}
              name="editorGridFailure"
            />
          }
          label="Force editor-grid failure state"
        />
        <FormControlLabel
          control={
            <Switch
              checked={debug?.editorGridLoading}
              onChange={handleChange}
              name="editorGridLoading"
            />
          }
          label="Force editor-grid loading state"
        />
      </FormGroup>
      <Divider />
      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={debug?.accountDebugTab}
              onChange={handleChange}
              name="accountDebugTab"
            />
          }
          label="Show account debug tab"
        />
      </FormGroup>
      <Button
        color="primary"
        variant="contained"
        onClick={() => onFetchMonthlyAccounts()}
        disabled={fetchMonthlyAccountsLoading}
      >
        Sync new CURO accounts
      </Button>
      <Box mt={2} />
      <Divider />
      {reviewId ? (
        <>
          <Box mt={2} mb={2}>
            <Button
              color="primary"
              variant="contained"
              onClick={async () => {
                await setIsJourneyToTrue({
                  variables: {
                    reviewId: validateUUID(reviewId)!,
                  },
                });
                updateReview({
                  variables: {
                    reviewId: validateUUID(reviewId)!,
                    isSubmittedFromOneview: false,
                  },
                }).then(({data}) => {
                  if (data?.updateReview?.ok) {
                    enqueueSnackbar('The current review is open', {variant: 'success'});
                    return;
                  }
                  enqueueSnackbar(
                    'An error occurred when re-opening the current review',
                    {variant: 'error'}
                  );
                });
              }}
              disabled={updateReviewLoading}
            >
              Re-open current review
            </Button>
          </Box>
          <Divider />
        </>
      ) : null}
      {syncData ? (
        <Box mt={2} mb={2}>
          <Typography variant="h6">Full CURO sync:</Typography>
          <Typography>
            Last sync started:{' '}
            {syncData.lastSyncStart
              ? moment(syncData.lastSyncStart).format('DD/MM/YYYY - HH:MM')
              : 'None'}
          </Typography>
          <Typography>
            Last sync ended:{' '}
            {syncData.lastSyncEnd
              ? moment(syncData.lastSyncEnd).format('DD/MM/YYYY - HH:MM')
              : 'None'}
          </Typography>
          <Box mt={2} />
          <Button
            color="primary"
            variant="contained"
            fullWidth
            onClick={() => startFullSync()}
            disabled={fullSyncLoading}
          >
            Start full CURO sync
          </Button>
        </Box>
      ) : (
        <Box mt={2} mb={2}>
          <Loading entityName="CURO sync" />
        </Box>
      )}
      {called && !fetchMonthlyAccountsLoading ? syncResult : null}
      <ShowIfRole roles={['dev', 'tester']}>
        <ChangeBusinessUnit type="dropdown" />
      </ShowIfRole>
    </FormControl>
  );
}

const styles = (theme: any) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

// @ts-ignore
const DialogTitle = withStyles(styles)((props) => {
  // @ts-ignore
  const {children, classes, onClose, ...other} = props;
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <MuiDialogTitle className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const throwException = () => {
  throw new ApiException('Test exception');
};

function AdminDialog(props: any) {
  const {open, handleClose} = props;
  const [error, setError] = useState(false);

  useEffect(() => {
    if (error) {
      throwException();
    }
  }, [error]);

  return (
    <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
      <DialogTitle>Admin settings</DialogTitle>
      <DialogContent dividers>
        <DebugSwitches />
        <Divider />
        <Box mt={1}>
          <Button variant="outlined" onClick={() => setError(true)}>
            Throw exception
          </Button>
        </Box>
        <Box mt={2} />
      </DialogContent>
    </Dialog>
  );
}

export default AdminDialog;
